import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval, useTimeout } from 'usehooks-ts';
import { v4 } from 'uuid';

import {
  LOGIN_STATE,
  WebexAuthenticationAPI,
  WebexAuthenticationConfig,
  WebexAuthorization,
  WEBEX_FAIL_REASON,
  WebexNotStarted,
  WebexRedirectStateType,
  WebexTokensType,
  CacheTokensParams,
} from './types';
import { ETL_CONFIGURATION_STATUS } from '../../components/configuration/types';
import { popupCenter, WEBEX_TAB_ID } from '../../utils/windowUtils';

const LOGIN_CHECK_DELAY = 1000;

const notStartedAuthentication: WebexNotStarted = {
  loginState: LOGIN_STATE.notStarted,
  intervalDelay: null,
  loginTimeout: null,
};

/**
 * Initiates custom login flow in order to retrieve Webex tokens.
 * Uses `popup` window from `window.open` API with postMessage combination in order to retrieve `code` & `state` query
 * params.
 * For more information about Webex login flow.
 * @see https://devportal.wxcc-us1.cisco.com/documentation/authentication
 */
export const useWebexAuthentication = (config: WebexAuthenticationConfig): WebexAuthenticationAPI => {
  const { appUrl, paramsUrl, tokensUrl, timeoutDuration, onSuccess, onFailed, handleTimeout } = config;
  const successCallback = useRef(onSuccess);
  const failedCallback = useRef(onFailed);
  const timeoutCallback = useRef(handleTimeout);

  const [authenticationState, setAuthenticationState] = useState<WebexAuthorization>(notStartedAuthentication);
  const tokensCache = useRef<Record<string, Record<string, Record<string, WebexTokensType>>>>({});

  const loginWindowRef = useRef<Window | null>(null);

  const cacheTokens = useCallback(({ clientId, clientSecret, scope, webexTokens }: CacheTokensParams) => {
    // empty values ('') are invalid
    if (!clientId || !clientSecret || !scope) {
      return;
    }

    if (tokensCache.current[clientId]?.[clientSecret]?.[scope]) {
      // dont overwrite previously stored tokens
      return;
    }

    if (!tokensCache.current[clientId]) {
      tokensCache.current[clientId] = {};
    }

    if (!tokensCache.current[clientId][clientSecret]) {
      tokensCache.current[clientId][clientSecret] = {};
    }

    tokensCache.current[clientId][clientSecret][scope] = webexTokens;
  }, []);

  /**
   * Handles retrieving tokens from Webex.
   */
  useEffect(() => {
    const { loginState } = authenticationState;
    if (loginState !== LOGIN_STATE.retrievingTokens) {
      return;
    }

    const { tokenParams } = authenticationState;

    axios
      .post(tokensUrl, tokenParams)
      .then((response) => {
        const webexTokens = {
          ...response.data,
          startTime: Date.now(),
          state: ETL_CONFIGURATION_STATUS.authorized,
        } as WebexTokensType;

        // store tokens for restore
        const { scope } = authenticationState;
        cacheTokens({
          clientId: tokenParams.client_id,
          clientSecret: tokenParams.client_secret,
          scope,
          webexTokens,
        });

        setAuthenticationState({
          loginState: LOGIN_STATE.success,
          webexTokens,
          intervalDelay: null,
          loginTimeout: null,
        });
        successCallback.current();
      })
      .catch((error) => {
        setAuthenticationState({
          loginState: LOGIN_STATE.failed,
          intervalDelay: null,
          loginTimeout: null,
        });
        failedCallback.current(WEBEX_FAIL_REASON.tokenRequestFailed);

        // eslint-disable-next-line no-console
        console.error(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationState.loginState]);

  useInterval(() => {
    const isInProgress = !(loginWindowRef.current == null || loginWindowRef.current?.closed);

    const newLoginState = isInProgress ? LOGIN_STATE.inProgress : LOGIN_STATE.interrupted;
    // dont re-render when there is no state change
    if (newLoginState === authenticationState.loginState) {
      return;
    }

    setAuthenticationState((prevState) => {
      const { loginState } = prevState;
      if (!(LOGIN_STATE.initiated === loginState || LOGIN_STATE.inProgress === loginState)) {
        return prevState;
      }

      if (isInProgress) {
        return {
          ...prevState,
          loginState: LOGIN_STATE.inProgress,
        };
      }

      return {
        loginState: LOGIN_STATE.interrupted,
        intervalDelay: null,
        loginTimeout: null,
      };
    });
  }, authenticationState.intervalDelay);

  useTimeout(() => {
    loginWindowRef.current?.close();
    timeoutCallback.current();
    setAuthenticationState({
      loginState: LOGIN_STATE.interrupted,
      intervalDelay: null,
      loginTimeout: null,
    });
  }, authenticationState.loginTimeout);

  /**
   * Handles processing messages received from `window.postMessage` API.
   * Adds event listener only when `initialState` is defined.
   */
  useEffect(() => {
    const { loginState } = authenticationState;
    if (loginState !== LOGIN_STATE.inProgress) {
      return () => {
        // noop
      };
    }

    const { codeParams, initialState, clientSecret } = authenticationState;

    const processMessage = (event: MessageEvent<any>) => {
      const { data } = event;
      if (!data || !data.payload || data.type !== 'webexcc-oauth-redirect-state') {
        return;
      }

      const { code, loginState: loginStateFromRedirect, state } = data.payload as WebexRedirectStateType;

      if (
        loginStateFromRedirect === LOGIN_STATE.failed ||
        !code ||
        !state ||
        !codeParams || // codeParams are not defined (message received on newly rendered component)
        !initialState || // request was performed without state parameter which should prevent CSRF attacks
        initialState !== state // state param is not same as in initial request - potential CSRF attack
      ) {
        setAuthenticationState({
          loginState: LOGIN_STATE.failed,
          intervalDelay: null,
          loginTimeout: null,
        });

        failedCallback.current(WEBEX_FAIL_REASON.codeRequestFailed);
        return;
      }

      setAuthenticationState({
        loginState: LOGIN_STATE.retrievingTokens,
        intervalDelay: null,
        loginTimeout: null,
        scope: codeParams.scope,
        tokenParams: {
          client_id: codeParams.client_id,
          redirect_uri: codeParams.redirect_uri,
          client_secret: clientSecret,
          grant_type: 'authorization_code',
          code,
        },
      });
    };

    window.addEventListener('message', processMessage, false);
    return () => {
      window.removeEventListener('message', processMessage, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationState.loginState]);

  /**
   * Handles triggering of Webex login, requires `clientId`, `clientSecret` and `scope` to be defined from user.
   */
  const triggerWebexLogin = useCallback((clientId: string, secret: string, scope: string) => {
    const newCodeParams = {
      response_type: 'code',
      redirect_uri: `${appUrl}/webexcc-oauth-redirect.html`,
      client_id: clientId,
      scope,
      state: v4(),
    };
    const loginURL = `${paramsUrl}?${new URLSearchParams(newCodeParams).toString()}`;

    /**
     * dont open multiple login screens
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/open#best_practices
     */
    if (loginWindowRef.current == null || loginWindowRef.current?.closed) {
      loginWindowRef.current = popupCenter(loginURL, WEBEX_TAB_ID);
    }

    loginWindowRef.current?.focus();

    setAuthenticationState({
      loginState: LOGIN_STATE.initiated,
      clientSecret: secret,
      codeParams: newCodeParams,
      intervalDelay: LOGIN_CHECK_DELAY, // set maximum timeout to trigger failed state?
      initialState: newCodeParams.state,
      loginTimeout: timeoutDuration, // interrupt login session after some time.
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Callback used to clear previously stored state, and reset state of this hook into `LoginState.NOT_STARTED`.
   */
  const resetAuthentication = useCallback((params?: CacheTokensParams) => {
    // store previous state, this is useful when data are fetched from BE and hook has no previous login.
    if (params) {
      cacheTokens(params);
    }

    setAuthenticationState(notStartedAuthentication);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wasPreviouslyLoggedIn = useCallback((clientId: string, secret: string, scope: string) => {
    return !!tokensCache.current[clientId]?.[secret]?.[scope];
  }, []);

  const restorePreviousLogin = useCallback((clientId: string, secret: string, scope: string) => {
    const webexTokens = tokensCache.current[clientId]?.[secret]?.[scope];
    if (!webexTokens) {
      console.warn('Restore tokens called for invalid combination of clientId/secret/scope');
      return; // noop
    }

    setAuthenticationState({
      loginState: LOGIN_STATE.success,
      webexTokens,
      intervalDelay: null,
      loginTimeout: null,
    });
  }, []);

  const { loginState } = authenticationState;
  return {
    triggerWebexLogin,
    loginState,
    webexTokens: loginState === LOGIN_STATE.success ? authenticationState.webexTokens : undefined,
    resetAuthentication,
    wasPreviouslyLoggedIn,
    restorePreviousLogin,
  };
};
